import { useMemo } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

export const useEventProcessing = (
  events: any,
  weekRange: { start: dayjs.Dayjs; end: dayjs.Dayjs },
  calendarDate: Date,
  checkedEvent: any
) => {
  // Weekly events for the current week
  const weeklyEvents = useMemo(() => {
    if (!events || typeof events !== 'object') {
      return [];
    }

    const allAvailableEvents = Object.entries(events).flatMap(
      ([calendarType, eventList]) => {
        if (!Array.isArray(eventList)) {
          return [];
        }
        return eventList.map((event: any) => ({
          id: event.id,
          title: event.summary,
          location: event.location,
          htmlLink: event.description?.link,
          start: dayjs(event.start?.dateTime || event.start?.date),
          end: dayjs(event.end?.dateTime || event.end?.date),
        }));
      }
    );

    return allAvailableEvents
      .filter(event => {
        if (!event.start.isValid() || !weekRange.start || !weekRange.end) {
          return false;
        }
        return event.start.isBetween(
          weekRange.start,
          weekRange.end,
          null,
          '[]'
        );
      })
      .sort((a, b) => a.start.diff(b.start))
      .map(event => {
        let dateTime;
        if (event.start.isSame(event.end, 'day')) {
          dateTime = `${event.start.format('dddd, MMMM D')}`;
        } else {
          dateTime = `${event.start.format('dddd, MMMM D')} - ${event.end.format(
            'dddd, MMMM D'
          )}`;
        }
        return {
          ...event,
          dateTime,
        };
      });
  }, [events, weekRange]);

  // Calendar events for the current month
  const calendarEvents = useMemo(() => {
    if (
      !events ||
      typeof events !== 'object' ||
      !checkedEvent ||
      typeof checkedEvent !== 'object'
    ) {
      return [];
    }

    const currentMonthStart = dayjs(calendarDate).startOf('month');
    const currentMonthEnd = dayjs(calendarDate).endOf('month');

    let eventsList: any[] = [];

    Object.entries(checkedEvent).forEach(([key, checked]) => {
      if (checked && events[key] && Array.isArray(events[key])) {
        const filteredEvents = events[key]
          .filter((event: any) => {
            if (!event.start) return false;
            const startDate = dayjs(event.start?.dateTime || event.start?.date);
            return (
              startDate.isValid() &&
              startDate.isBetween(
                currentMonthStart,
                currentMonthEnd,
                null,
                '[]'
              )
            );
          })
          .map((event: any) => ({
            id: event.id,
            title: event.summary,
            start: event.start?.dateTime || event.start?.date,
            end: event.end?.dateTime || event.end?.date,
            backgroundColor: event.backgroundColor || '#3788d8',
            borderColor: event.borderColor || '#3788d8',
            extendedProps: {
              location: event.location,
              description: event.description?.description,
              htmlLink: event.description?.link,
            },
          }));

        eventsList = eventsList.concat(filteredEvents);
      }
    });

    return eventsList;
  }, [events, checkedEvent, calendarDate]);

  return {
    weeklyEvents,
    calendarEvents,
  };
};
